import { api } from './client'
import { specializedJobsOn, runReportAsJob } from './reportJobRun'

/**
 * AR Report — wraps cb-api's /reports/ar/* endpoints which proxy CB's
 * legacy ARReportController. Two-step query model (matches CB):
 *   1. filter()  → returns result-set IDs + counts
 *   2. getRows() → fetches paged rows for those result-set IDs
 *
 * The data shape mirrors CB's ARColumns / ARReportGridSettings, deep-camelized.
 */

const arReportService = {
  /** GET /reports/ar/columns — column definitions for a given view mode */
  async getColumns(viewMode = 'Matter', showDetails = false) {
    const data = await api.get('/reports/ar/columns', { gridType: viewMode, showDetails })
    return data?.data || null
  },

  /** POST /reports/ar/filter — kicks off the query, returns result-set IDs */
  async filter(filterData) {
    const data = await api.post('/reports/ar/filter', filterData)
    return data?.data || null
  },

  /** POST /reports/ar/data — paged rows */
  async getRows({
    invoicePaymentResultSetId, viewByResultSetId, viewByRowCount = 0,
    startIndex = 0, pageSize = 500, paymentClientType = 0, viewByObjectType = 0,
    viewMode = 'Matter', showDetails = false, endDate = '',
  }) {
    const data = await api.post('/reports/ar/data', {
      invoicePaymentResultSetId, viewByResultSetId, viewByRowCount,
      startIndex, pageSize, paymentClientType, viewByObjectType,
      viewMode, showDetails, endDate,
    })
    return data?.data || null
  },

  /**
   * Convenience: filter + page through ALL rows.
   * Returns { rows, columnSettings, partialData }.
   */
  async runReport(filterData, { onProgress, signal, pageSize = 500 } = {}) {
    const columnSettings = await arReportService.getColumns(filterData.viewMode, !!filterData.showDetails)
    const partial = await arReportService.filter(filterData)
    if (!partial) throw new Error('AR filter returned no result')

    const total = partial.invoicePaymentRowCount || 0
    const allRows = []
    let startIndex = 0
    let isClientGridMatterDetailMode = false

    while (startIndex < total) {
      if (signal?.aborted) throw new Error('aborted')
      const page = await arReportService.getRows({
        invoicePaymentResultSetId: partial.invoicePaymentResultSetId,
        viewByResultSetId: partial.viewByResultSetId,
        viewByRowCount: total,
        startIndex,
        pageSize,
        paymentClientType: partial.paymentClientType || 0,
        viewByObjectType: partial.viewByObjectType || 0,
        viewMode: filterData.viewMode || 'Matter',
        showDetails: !!filterData.showDetails,
        endDate: filterData.endDate ? new Date(filterData.endDate).toISOString() : '',
      })
      if (!page) break
      const rows = page.rows || []
      allRows.push(...rows)
      isClientGridMatterDetailMode = page.isClientGridMatterDetailMode ?? isClientGridMatterDetailMode
      startIndex += rows.length
      if (rows.length === 0) break // safety: avoid infinite loop
      onProgress?.(allRows.length, total)
      // CC-49752: `total` counts INVOICE/PAYMENT rows, but in summary mode the
      // endpoint returns MATTER-aggregated rows (fewer). Paging past the first
      // short page made the legacy endpoint re-return later matters with their
      // FULL totals, which the CC-49597 dedup then SUMMED — doubling/tripling
      // those matters and inflating the grand total (verified live on vladdemo:
      // V1 $309,541.46 vs V2 $340,798.98, per-matter ×2/×3). A short page means
      // the row set is exhausted — stop.
      if (rows.length < pageSize) break
    }

    return { rows: allRows, columnSettings, partialData: partial, isClientGridMatterDetailMode }
  },

  /**
   * Run the full AR report. When the job system is on (spec 055) this runs as a
   * background job — cb-api's POST /reports/ar/run does the whole multi-step
   * (columns + filter + all pages) server-side, so the client isn't bound to the
   * ~100s edge timeout — and it lands in the tray. Same shape as runReport:
   * { rows, columnSettings, partialData, isClientGridMatterDetailMode }.
   */
  async run(filterData, { reportId } = {}) {
    if (specializedJobsOn()) {
      const result = await runReportAsJob({
        kind: 'specialized',
        actionKey: 'ar',
        reportId,
        endpoint: { method: 'POST', path: '/reports/ar/run-all', body: filterData },
      })
      return result?.data || { rows: [], columnSettings: null }
    }
    // Flag off → the client-side multi-step (no tray, but keeps working locally).
    return arReportService.runReport(filterData)
  },

  /** Build an export URL — CSV or XLSX. Browser hits cb-api directly with Bearer token. */
  exportUrl(filterData, format /* 'csv' | 'xlsx' */, { sortColumn = '', ascending = true } = {}) {
    const params = new URLSearchParams()
    params.set('filterString', JSON.stringify({
      companyId: filterData.companyId || null,
      origId: filterData.origId || null,
      matterId: filterData.matterId || null,
      timekeeperId: filterData.timekeeperId || null,
      viewMode: filterData.viewMode || 'Matter',
      postedFilter: filterData.postedFilter ?? null,
      showDetails: !!filterData.showDetails,
      endDate: filterData.endDate || null,
    }))
    params.set('sortedColumnDataField', sortColumn)
    params.set('isSortedAscendingArg', String(!!ascending))
    return `/api/v1/reports/ar/export.${format}?${params.toString()}`
  },
}

export default arReportService
